import { FC, ReactElement, useEffect, useMemo, useState } from "react";
import CardComponent from "../../../../common/components/card/card.component";
import FormFieldComponent from "../../../../common/components/form/field/form-field.component";
import DateTimeInputComponent from "../../../../common/components/form/input/date-time/date-time-input.component";
import InputComponent from "../../../../common/components/form/input/input.component";
import NumericInputComponent from "../../../../common/components/form/input/numeric-input/numeric-input.component";
import useForm from "../../../../common/components/form/use-form";
import Column from "../../../../common/components/grid/column";
import Row from "../../../../common/components/grid/row";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import mileageTranslationsHelper from "../../../../languages/mileage-translations.helper";
import mileageBreadcrumbsHelper from "../../common/breadcrumbs/mileage-breadcrumbs.helper";
import MileageAddRoutesComponent from "../common/components/address/mileage-add-routes.component";
import { MileageAddRouteItem } from "../common/components/address/mileage-add-routes.types";
import MileageAddWorkerComponent from "../common/components/worker/mileage-add-worker-select.component";
import mileageAddFormHelper from "../common/form/mileage-add-form.helper";
import MileageAddAddress from "../common/types/mileage-add-address";
import MileageAddBasicProps from "../common/types/mileage-add-basic-props";
import MileageAddSummaryComponent from "../common/components/summary/mileage-add-summary.component";
import MileageAddWorker from "../common/types/mileage-add-worker";
import CargoCompanySingleSelectComponent from "../../../../common/components/form/select/cargo-company/cargo-company-single-select.component";
import useCargoCompanyList from "../../../../common/services/cargo-company/cargo-company/list/use-cargo-company-list";
import useAbort from "../../../../common/hooks/use-abort";
import mileageAddAddressFactory from "../common/factory/mileage-add-address.factory";
import useMileageSettingsDetails from "../../../../common/services/mileage/settings/details/use-mileage-settings-details";
import MileageSettingsDetailsParams from "../../../../common/services/mileage/settings/details/mileage-settings-details-params";
import MileageAddMileageNumberDictionaryInputComponent from "../common/components/mileage-number-dictionary-input/mileage-add-mileage-number-dictionary-input.component";
import mileageAddFormFactory from "../common/form/mileage-add-form.factory";
import MileageAddVehicleTypeSelectComponent from "../common/components/vehicle-type-select/mileage-add-vehicle-type-select.component";
import { round } from "lodash";
import useContractsMileageCargoCompanyDetails from "../../../../common/services/contracts/mileage/cargo-company-details/use-contracts-mileage-cargo-company-details";
import ContractsMileageCargoCompanyDetailsParams from "../../../../common/services/contracts/mileage/cargo-company-details/contracts-mileage-cargo-company-details-params";
import MileageNumberCreationModel from "../../../../common/types/mileage-number-creation-model";
type RailyMileageAddProps = MileageAddBasicProps;

const RailyMileageAddComponent: FC<RailyMileageAddProps> = (props) => {
  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();
  const translations = mileageTranslationsHelper.getMileageAddTranslations();

  useEffect(() => {
    const breadcrumbs = mileageBreadcrumbsHelper.getMileageAddBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.mileageAdd);

  const cargoCompanyList = useCargoCompanyList();
  const cargoCompanyListAbort = useAbort();

  const mileageSettingsDetails = useMileageSettingsDetails();
  const mileageSettingsDetailsAbort = useAbort();

  const contractMileageDetails = useContractsMileageCargoCompanyDetails();
  const contractMileageDetailsAbort = useAbort();

  const [worker, setWorker] = useState<MileageAddWorker | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [routesChanged, setRoutesChanged] = useState(false);

  const [mileageAddRoutes, setMileageAddRoutes] = useState<
    MileageAddRouteItem[]
  >([]);

  const form = useForm({
    emptyValues: mileageAddFormHelper.getDefaultFormData(),
    validationDefinition: mileageAddFormHelper.getValidationDefinition(),
  });

  const loadMileageSettingsDetails = (cargoCompanyUuid: string) => {
    const params: MileageSettingsDetailsParams = {
      cargoCompanyUuid: cargoCompanyUuid,
    };

    mileageSettingsDetails.load(params, mileageSettingsDetailsAbort.signal);
  };

  const loadCargoMileageContract = (
    cargoCompanyUuid: string,
    validityDate?: Date,
  ) => {
    const loadParams: ContractsMileageCargoCompanyDetailsParams = {
      cargoCompanyUuid: cargoCompanyUuid,
      validAt: validityDate,
    };

    contractMileageDetails.load(loadParams, contractMileageDetailsAbort.signal);
  };

  useEffect(() => {
    cargoCompanyList.load({}, cargoCompanyListAbort.signal);
  }, []);

  useEffect(() => {
    if (!form.values.companyUuid) {
      return;
    }

    loadMileageSettingsDetails(form.values.companyUuid);

    return mileageSettingsDetailsAbort.revoke;
  }, [form.values.companyUuid]);

  useEffect(() => {
    if (!form.values.companyUuid) return;

    loadCargoMileageContract(
      form.values.companyUuid,
      form.values.mileageDate ?? undefined,
    );
  }, [form.values.companyUuid, form.values.mileageDate]);

  useEffect(() => {
    form.setValue(
      "settings",
      mileageAddFormFactory.createSettings(mileageSettingsDetails.data),
    );
  }, [mileageSettingsDetails.data]);

  const restoreForm = () => {
    setWorker(null);
    setCompanyName("");
    setMileageAddRoutes([]);
    form.restore();
  };

  const onRoutesChange = (routeItems: MileageAddRouteItem[]) => {
    setMileageAddRoutes(routeItems);

    const seq: MileageAddAddress[] =
      mileageAddAddressFactory.createFromRouteItems(routeItems);

    form.setValue("addressSequence", seq);

    setRoutesChanged(true);
  };

  const onWorkerChange = (worker: MileageAddWorker | null) => {
    setWorker(worker);
    setMileageAddRoutes([]);

    form.setValue("workerUuid", worker?.uuid ?? "");
    form.setValue("addressSequence", []);
  };

  const passengerAddresses = useMemo(() => {
    return mileageAddAddressFactory.createPassengerAddresses(worker);
  }, [worker, selectedAppLanguage]);

  useEffect(() => {
    form.validate("addressSequence");
  }, [mileageAddRoutes]);

  const manualInputComponent = (
    <InputComponent
      value={form.values.mileageNumber}
      onChange={(value) => {
        form.setValue("mileageNumber", value);
      }}
      onBlur={() => {
        form.validate("mileageNumber");
      }}
      idForTesting="raily-mileage-add-mileage-number-manual"
      placeholder={translations.mileageNumber.selectPlaceholder}
      hasError={!!form.validationResults.mileageNumber.errorMessage}
    />
  );

  const autoInputComponent = (
    <InputComponent
      value={undefined}
      idForTesting="raily-mileage-add-mileage-number-auto"
      placeholder={translations.mileageNumber.autoInputPlaceholder}
      isDisabled
    />
  );

  const dictionaryInputComponent = (
    <MileageAddMileageNumberDictionaryInputComponent
      cargoCompanyUuid={form.values.companyUuid}
      value={form.values.mileageNumber}
      onChange={(value) => {
        form.setValue("mileageNumber", value);
      }}
      onBlur={() => {
        form.validate("mileageNumber");
      }}
      idForTesting="raily-mileage-add-mileage-number-manual"
      placeholder={translations.mileageNumber.dictionaryInputPlaceholder}
      hasError={!!form.validationResults.mileageNumber.errorMessage}
      isClearable={!form.values.settings.isMileageNumberRequired}
    />
  );

  const mileageNumberInputComponent = useMemo((): ReactElement => {
    switch (form.values.settings?.mileageNumberCreationModel) {
      case MileageNumberCreationModel.AUTO:
        return autoInputComponent;
      case MileageNumberCreationModel.DICT:
        return dictionaryInputComponent;
      default:
        return manualInputComponent;
    }
  }, [
    form.values.settings,
    form.values.mileageNumber,
    form.validationResults.mileageNumber,
    selectedAppLanguage,
    manualInputComponent,
    autoInputComponent,
    dictionaryInputComponent,
  ]);

  const onRouteFetched = (value: number) => {
    if (routesChanged) {
      form.setValue("distance", value);
      form.setValue("osrmDistance", value);
    }
  };

  return (
    <>
      <div className="mileage_add">
        <HeadingComponent
          title={translations.header.headingLabel}
          actions={props.changeViewButtons}
        />
        <Row>
          <Column xl={7}>
            <CardComponent>
              <Row>
                <Column lg={4}>
                  <FormFieldComponent
                    label={translations.company.headingLabel}
                    isRequired
                    errorMessage={
                      form.validationResults.companyUuid.errorMessage
                    }
                  >
                    <CargoCompanySingleSelectComponent
                      placeholder={translations.company.selectPlaceholder}
                      cargoCompanyList={cargoCompanyList.data.data}
                      selectedCargoCompanyUuid={form.values.companyUuid}
                      onChange={(value) => {
                        restoreForm();
                        form.setValue("companyUuid", value?.uuid ?? "");
                        setCompanyName(value?.displayName ?? "");
                      }}
                      isError={
                        !!form.validationResults.companyUuid.errorMessage
                      }
                      isLoading={cargoCompanyList.isLoading}
                      isDisabled={cargoCompanyList.isError}
                      onBlur={() => form.validate("companyUuid")}
                      idForTesting="raily-mileage-add-cargo-company"
                    />
                  </FormFieldComponent>
                </Column>
                <Column lg={4}>
                  <FormFieldComponent
                    label={translations.workers.headingLabel}
                    isRequired
                    errorMessage={
                      form.validationResults.workerUuid.errorMessage
                    }
                  >
                    <MileageAddWorkerComponent
                      worker={worker}
                      onWorkerChange={onWorkerChange}
                      onBlur={() => {
                        form.validate("workerUuid");
                      }}
                      workerCompanyUuid={form.values.companyUuid}
                      hasError={
                        !!form.validationResults.workerUuid.errorMessage &&
                        form.values.companyUuid !== null
                      }
                      idForTesting="raily-mileage-add-worker"
                    />
                  </FormFieldComponent>
                </Column>
                <Column lg={4}>
                  <FormFieldComponent
                    label={translations.date.headingLabel}
                    isRequired
                    errorMessage={
                      form.validationResults.mileageDate.errorMessage
                    }
                  >
                    <DateTimeInputComponent
                      date={form.values.mileageDate}
                      onChange={(value) => {
                        form.setValue("mileageDate", value);
                      }}
                      onBlur={() => {
                        form.validate("mileageDate");
                      }}
                      idForTesting="raily-mileage-add-mileage-date"
                      placeholder={translations.date.selectPlaceholder}
                      hasError={
                        !!form.validationResults.mileageDate.errorMessage
                      }
                    />
                  </FormFieldComponent>
                </Column>
              </Row>
              <Row>
                <Column lg={12}>
                  <FormFieldComponent
                    label={translations.routes.headingLabel}
                    isRequired
                    errorMessage={
                      form.validationResults.addressSequence.errorMessage
                    }
                  >
                    <MileageAddRoutesComponent
                      cargoCompanyUuid={form.values.companyUuid}
                      isAddButtonDisabled={
                        !form.values.companyUuid || !form.values.workerUuid
                      }
                      additionalAddresses={passengerAddresses}
                      routes={mileageAddRoutes}
                      onRoutesChange={onRoutesChange}
                      idForTesting="raily-mileage-add-routes"
                    />
                  </FormFieldComponent>
                </Column>
              </Row>
              <Row>
                <Column>
                  <FormFieldComponent
                    label={translations.vehicleType.headingLabel}
                    errorMessage={
                      form.validationResults.vehicleType.errorMessage
                    }
                  >
                    <MileageAddVehicleTypeSelectComponent
                      vehicleType={form.values.vehicleType}
                      onVehicleTypeChange={(type) => {
                        form.setValue("vehicleType", type);
                      }}
                      onBlur={() => {
                        form.validate("vehicleType");
                      }}
                    />
                  </FormFieldComponent>
                </Column>
              </Row>
              <Row>
                <Column lg={6}>
                  <FormFieldComponent
                    label={translations.cardNumber.headingLabel}
                    isRequired={
                      !form.values.settings ||
                      form.values.settings.isCardNumberRequired
                    }
                    errorMessage={
                      form.validationResults.cardNumber.errorMessage
                    }
                  >
                    <InputComponent
                      value={form.values.cardNumber}
                      onChange={(value) => {
                        form.setValue("cardNumber", value);
                      }}
                      onBlur={() => {
                        form.validate("cardNumber");
                      }}
                      idForTesting="raily-mileage-add-card-number"
                      placeholder={translations.cardNumber.selectPlaceholder}
                      hasError={
                        !!form.validationResults.cardNumber.errorMessage
                      }
                    />
                  </FormFieldComponent>
                </Column>
                <Column lg={6}>
                  <FormFieldComponent
                    label={translations.mileageNumber.headingLabel}
                    isRequired={
                      !form.values.settings ||
                      form.values.settings.isMileageNumberRequired
                    }
                    errorMessage={
                      form.validationResults.mileageNumber.errorMessage
                    }
                  >
                    {mileageNumberInputComponent}
                  </FormFieldComponent>
                </Column>
              </Row>
              <Row>
                <Column lg={6}>
                  <FormFieldComponent
                    label={translations.distance.headingLabel}
                    isRequired
                    errorMessage={form.validationResults.distance.errorMessage}
                  >
                    <NumericInputComponent
                      value={round(form.values.distance)}
                      onChange={(value) => {
                        form.setValue("distance", value);
                      }}
                      onBlur={() => {
                        form.validate("distance");
                      }}
                      isIntegerOnly
                      idForTesting="raily-mileage-add-distance"
                      placeholder={translations.distance.selectPlaceholder}
                      hasError={!!form.validationResults.distance.errorMessage}
                    />
                  </FormFieldComponent>
                </Column>
                <Column lg={6}>
                  <FormFieldComponent
                    label={translations.commissionNumber.headingLabel}
                    errorMessage={
                      form.validationResults.commissionNumber.errorMessage
                    }
                  >
                    <InputComponent
                      value={form.values.commissionNumber}
                      onChange={(value) => {
                        form.setValue("commissionNumber", value);
                      }}
                      onBlur={() => {
                        form.validate("commissionNumber");
                      }}
                      idForTesting="raily-mileage-add-commission-number"
                      placeholder={
                        translations.commissionNumber.selectPlaceholder
                      }
                      hasError={
                        !!form.validationResults.commissionNumber.errorMessage
                      }
                    />
                  </FormFieldComponent>
                </Column>
              </Row>
              <Row>
                <Column lg={12}>
                  <FormFieldComponent
                    label={translations.notes.headingLabel}
                    errorMessage={form.validationResults.notes.errorMessage}
                  >
                    <InputComponent
                      value={form.values.notes}
                      onChange={(value) => {
                        form.setValue("notes", value);
                      }}
                      onBlur={() => {
                        form.validate("notes");
                      }}
                      idForTesting="raily-mileage-add-notes"
                      placeholder={translations.notes.selectPlaceholder}
                      hasError={!!form.validationResults.notes.errorMessage}
                    />
                  </FormFieldComponent>
                </Column>
              </Row>
            </CardComponent>
          </Column>
          <Column xl={5}>
            <MileageAddSummaryComponent
              workerName={worker?.displayName}
              companyName={companyName}
              formData={form.values}
              validateForm={form.validateAll}
              restoreForm={restoreForm}
              onRouteFetched={onRouteFetched}
              mileageContract={contractMileageDetails.data}
            />
          </Column>
        </Row>
      </div>
    </>
  );
};

export default RailyMileageAddComponent;
